import React, { useEffect, useState, useCallback } from "react";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { organization, Submit, SuperAdmin } from "../../Constant";
import { useSelectedCenter } from "../CenterContext";
import { useSelectedOrganization } from "../OrganizationContext";
import {
  All,
  CENTER_IDS,
  CENTER_NAME,
  checkStatusCodeSuccess,
  DEFAULT_CENTER,
  getItem,
  getLocalItem,
  setLocalItem,
  removeLocalItem,
  Null,
  SELECTED_CENTER_ID,
  setItem,
} from "../../Constant/common";
import { centerApi } from "../../Api/common";
import { toast } from "react-toastify";
import { SelectPlaceHolder } from "../../Constant/validation";
import { staffEnums } from "../../Constant/Staff/staff";
import { listOfOrganizationApi } from "../../Api/OrganizationApi";
import { usePageLoader } from "../../_helper/loading/LoadingContext";

const formFields = {
  organization: "organization",
};

const EMPTY_ARRAY = [];

const GlobalConfig = () => {
  const isSuperAdmin = getLocalItem("role") === SuperAdmin;
  const navigate = useNavigate();
  const { setLoading } = usePageLoader();
  const { setSelectedCenter } = useSelectedCenter();
  const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();
  const storedCenterIds = getLocalItem(CENTER_IDS);
  const { fromSetting } = useLocation().state || {};
  const centerDataStorage = React.useMemo(
    () => (storedCenterIds ? JSON.parse(storedCenterIds) : EMPTY_ARRAY),
    [storedCenterIds],
  );
  const [centerData, setCenterData] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);

  const fetchCenter = React.useCallback(
    async (orgId) => {
      if (!orgId && isSuperAdmin) return;

      setLoading(true);
      await centerApi({ condition: { organizationId: orgId } })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp.statusCode)) {
            let data = resp.data.map((item) => ({
              value: String(item.id),
              label: String(item.center_name),
              id: String(item.id),
            }));

            if (resp.data.length > 0) {
              const orgName = resp.data[0]?.organization?.organization_name;
              if (orgName) {
                setLocalItem("organization_name", orgName);
              }
            }

            if (centerDataStorage.includes(All)) {
              data.unshift({ value: All, label: All, id: All });
            }

            setCenterData(data);
          } else {
            setCenterData([]);
            toast.error("Failed to load centers.");
          }
        })
        .catch(() => {
          setCenterData([]);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [centerDataStorage, setLoading, isSuperAdmin],
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      const orgId = getLocalItem("organizationId");
      if (orgId) {
        setSelectedOrganization(Number(orgId));
        fetchCenter(Number(orgId));
      }
      return;
    }

    setLoading(true);
    listOfOrganizationApi()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const options = resp?.data?.listOfOrganizations?.map((item) => ({
            value: item.id,
            label: item.organization_name,
          }));
          setOrganizationList(options);
        }
      })
      .finally(() => setLoading(false));
  }, [setLoading, isSuperAdmin, fetchCenter, setSelectedOrganization]);
  const initialCenter = getLocalItem(SELECTED_CENTER_ID);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      center: initialCenter !== Null ? Number(initialCenter) : All,
    },
    onSubmit: (values) => {
      const newCenterID =
        values.center === All || values.center === null ? null : Number(values.center);

      setItem(SELECTED_CENTER_ID, newCenterID);
      setSelectedCenter(newCenterID);

      const selectedCenterName = centerData.find(
        (item) => item.value === String(newCenterID),
      )?.label;

      setItem(CENTER_NAME, selectedCenterName);

      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate(fromSetting !== undefined ? "/setting" : -1);
      }, 1000);
    },
  });

  const { setFieldValue } = formik;

  useEffect(() => {
    const finalCenterId = getItem(SELECTED_CENTER_ID)
      ? getItem(SELECTED_CENTER_ID) !== Null
        ? getItem(SELECTED_CENTER_ID)
        : getLocalItem(DEFAULT_CENTER)
      : getLocalItem(DEFAULT_CENTER);

    if (centerData.length > 0) {
      const savedCenter = finalCenterId !== Null ? finalCenterId : All;

      if (savedCenter) {
        setFieldValue("center", savedCenter);
      } else {
        const firstCenter = centerData[0].value;
        const newCenterId = firstCenter !== All ? Number(firstCenter) : null;

        setItem(SELECTED_CENTER_ID, newCenterId);
        setSelectedCenter(newCenterId);
        setFieldValue("center", newCenterId);
      }
    }
  }, [centerData, setFieldValue, setSelectedCenter]);

  useEffect(() => {
    if (selectedOrganization) {
      fetchCenter(selectedOrganization);
      setFieldValue("center", "");
    }
  }, [selectedOrganization, fetchCenter, setFieldValue]);

  const handleOrganizationChange = useCallback(
    (_field, value) => {
      const orgId = value === "" || value === null ? null : Number(value);
      setSelectedOrganization(orgId);

      if (orgId) {
        const org = organizationList.find((o) => Number(o.value) === orgId);
        if (org) {
          setLocalItem("organization_name", org.label);
        }
      } else {
        removeLocalItem("organization_name");
      }

      // Dispatch custom event to notify OrganizationContext
      window.dispatchEvent(
        new CustomEvent("organizationChange", {
          detail: { organizationId: orgId },
        }),
      );
    },
    [setSelectedOrganization, organizationList],
  );

  const handleCenterChange = useCallback(
    (field, value) => {
      setFieldValue(field, value);
    },
    [setFieldValue],
  );

  return (
    <div className="card section mb-3 p-4">
      <form onSubmit={formik.handleSubmit}>
        <div className="row mb-3">
          {isSuperAdmin && (
            <div className="col-lg-4">
              <BaseSelect
                id={formFields.organization}
                name={formFields.organization}
                label={organization}
                options={organizationList}
                placeholder={SelectPlaceHolder(staffEnums.ORGANIZATION)}
                value={selectedOrganization}
                handleChange={handleOrganizationChange}
              />
            </div>
          )}

          <div className="col-lg-4">
            <BaseSelect
              id="center"
              name="center"
              label="Center"
              options={centerData}
              placeholder="Center"
              handleChange={handleCenterChange}
              value={formik.values.center}
            />
          </div>
        </div>

        <BaseButton type="submit" className="btn btn-pill btn-primary">
          {Submit}
        </BaseButton>
      </form>
    </div>
  );
};

export default GlobalConfig;
